import React, { useState, useCallback, useRef, useEffect } from 'react';
import styles from './CommandArgsBuilder.module.css';

interface CommandArgsBuilderProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  /** Returns a reason string if the arg is disallowed, null otherwise */
  checkDisallowed?: (arg: string) => string | null;
}

interface ArgPill {
  id: string;
  text: string;
  type: 'flag' | 'keyvalue' | 'positional';
}

function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Check whether all delimiters are balanced: quotes ("" ''), braces,
 * brackets, and parens.  Prevents premature pillification when a value
 * contains JSON, paths, or nested structures.
 */
function isBalanced(text: string): boolean {
  let inDouble = false;
  let inSingle = false;
  const stack: string[] = [];
  const openers: Record<string, string> = { '{': '}', '[': ']', '(': ')' };
  const closers = new Set(Object.values(openers));

  for (const ch of text) {
    if (inSingle) { if (ch === "'") inSingle = false; continue; }
    if (inDouble) { if (ch === '"') inDouble = false; continue; }
    if (ch === '"') { inDouble = true; continue; }
    if (ch === "'") { inSingle = true; continue; }
    if (ch in openers) { stack.push(openers[ch]); continue; }
    if (closers.has(ch)) {
      if (stack.length === 0 || stack[stack.length - 1] !== ch) return false;
      stack.pop();
    }
  }
  return !inDouble && !inSingle && stack.length === 0;
}

/** Does the token start with a recognised arg prefix? (--, -, /) */
function isArgLike(token: string): boolean {
  return /^(-{1,2}|\/)[a-zA-Z]/.test(token);
}

/** Does the token carry an inline value?  -key=val  /key:val  /key=val */
function hasInlineValue(token: string): boolean {
  if (!isArgLike(token)) return false;
  const afterPrefix = token.replace(/^-{1,2}|^\//, '');
  return afterPrefix.includes('=') || (token.startsWith('/') && afterPrefix.includes(':'));
}

/**
 * Tokenise a command-arg string respecting quoted spans.
 * Spaces inside double or single quotes are preserved within the token.
 */
function tokenise(input: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inDouble = false;
  let inSingle = false;

  for (const ch of input) {
    if (ch === '"' && !inSingle) {
      inDouble = !inDouble;
      current += ch;
    } else if (ch === "'" && !inDouble) {
      inSingle = !inSingle;
      current += ch;
    } else if (/\s/.test(ch) && !inDouble && !inSingle) {
      if (current) { tokens.push(current); current = ''; }
    } else {
      current += ch;
    }
  }
  if (current) tokens.push(current);
  return tokens;
}

function classifyToken(token: string): ArgPill['type'] {
  if (!isArgLike(token)) return 'positional';
  return hasInlineValue(token) ? 'keyvalue' : 'flag';
}

function makePill(text: string): ArgPill {
  return { id: generateId(), text, type: classifyToken(text) };
}

/**
 * Parse a stored args value into pills.
 * Accepts JSON string[] or legacy space-separated string.
 */
function parseArgs(value: string): ArgPill[] {
  if (!value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter((s): s is string => typeof s === 'string' && s.length > 0).map(makePill);
    }
  } catch { /* not JSON, fall through */ }
  // Legacy: space-separated (with quote-aware tokenisation)
  return tokenise(value).map(makePill);
}

/** Serialize pills as a JSON string[] for storage. */
function serializePills(pills: ArgPill[]): string {
  if (pills.length === 0) return '';
  return JSON.stringify(pills.map(p => p.text));
}

/**
 * Is the token complete enough to become a pill?
 * Any non-empty token with balanced quotes qualifies.
 */
function isComplete(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.length > 0 && isBalanced(trimmed);
}

/** Move an element within an array from one index to another. */
function reorder<T>(list: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...list];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  return result;
}

// ── Component ────────────────────────────────────────────────────

export function CommandArgsBuilder({ value, onChange, id, checkDisallowed }: CommandArgsBuilderProps) {
  const [pills, setPills] = useState<ArgPill[]>(() => parseArgs(value));
  const [inputValue, setInputValue] = useState('');
  const [rejection, setRejection] = useState<string | null>(null);

  // Selection state (set of pill ids)
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Drag state
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragSourceRef = useRef<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync pills from external value changes (e.g. dialog reopen with saved data)
  useEffect(() => {
    const externalPills = parseArgs(value);
    const currentSerialized = serializePills(pills);
    // Only reset if the external value differs from what we'd produce
    if (value !== currentSerialized) {
      setPills(externalPills);
      setInputValue('');
      setRejection(null);
      setSelected(new Set());
      setEditingId(null);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parent sync ──

  const updateParent = useCallback((newPills: ArgPill[]) => {
    setPills(newPills);
    onChange(serializePills(newPills));
  }, [onChange]);

  // ── Selection ──

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    lastClickedRef.current = null;
  }, []);

  // ── Inline editing ──

  const startEditing = useCallback((pillId: string) => {
    const pill = pills.find(p => p.id === pillId);
    if (!pill) return;
    setEditingId(pillId);
    setEditValue(pill.text);
    clearSelection();
    // Focus the edit input on next render
    requestAnimationFrame(() => editInputRef.current?.focus());
  }, [pills, clearSelection]);

  const commitEdit = useCallback(() => {
    if (!editingId) return;
    const trimmed = editValue.trim();
    if (trimmed && isComplete(trimmed)) {
      // Check disallow list
      if (checkDisallowed) {
        const reason = checkDisallowed(trimmed);
        if (reason) {
          setRejection(`"${trimmed}" is not allowed: ${reason}`);
          // Keep editing
          return;
        }
      }
      setRejection(null);
      const newPills = pills.map(p =>
        p.id === editingId ? { ...p, text: trimmed, type: classifyToken(trimmed) } : p
      );
      updateParent(newPills);
    } else if (!trimmed) {
      // Empty → delete the pill
      const newPills = pills.filter(p => p.id !== editingId);
      updateParent(newPills);
    }
    // else: invalid (unbalanced quotes etc) → revert silently
    setEditingId(null);
    setEditValue('');
  }, [editingId, editValue, pills, checkDisallowed, updateParent]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue('');
  }, []);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }, [commitEdit, cancelEdit]);

  const handleEditBlur = useCallback(() => {
    commitEdit();
  }, [commitEdit]);

  const handlePillClick = useCallback((e: React.MouseEvent, pillId: string) => {
    e.stopPropagation();

    const metaKey = e.metaKey || e.ctrlKey;

    if (e.shiftKey && lastClickedRef.current) {
      // Range select from last clicked to this one
      const lastIdx = pills.findIndex(p => p.id === lastClickedRef.current);
      const thisIdx = pills.findIndex(p => p.id === pillId);
      if (lastIdx >= 0 && thisIdx >= 0) {
        const lo = Math.min(lastIdx, thisIdx);
        const hi = Math.max(lastIdx, thisIdx);
        const range = new Set(metaKey ? selected : new Set<string>());
        for (let i = lo; i <= hi; i++) range.add(pills[i].id);
        setSelected(range);
      }
    } else if (metaKey) {
      // Toggle individual
      const next = new Set(selected);
      if (next.has(pillId)) next.delete(pillId);
      else next.add(pillId);
      setSelected(next);
      lastClickedRef.current = pillId;
    } else {
      // Single select (or deselect if already the only one selected)
      if (selected.size === 1 && selected.has(pillId)) {
        clearSelection();
      } else {
        setSelected(new Set([pillId]));
        lastClickedRef.current = pillId;
      }
    }
  }, [pills, selected, clearSelection]);

  // ── Copy / Cut / Paste ──

  const getSelectedText = useCallback((): string => {
    if (selected.size === 0) return '';
    // Preserve order
    return pills.filter(p => selected.has(p.id)).map(p => p.text).join(' ');
  }, [pills, selected]);

  const removeSelected = useCallback((): ArgPill[] => {
    const remaining = pills.filter(p => !selected.has(p.id));
    clearSelection();
    return remaining;
  }, [pills, selected, clearSelection]);

  const handleContainerKeyDown = useCallback((e: React.KeyboardEvent) => {
    const meta = e.metaKey || e.ctrlKey;
    if (!meta || selected.size === 0) return;

    if (e.key === 'c') {
      e.preventDefault();
      navigator.clipboard.writeText(getSelectedText());
    } else if (e.key === 'x') {
      e.preventDefault();
      navigator.clipboard.writeText(getSelectedText());
      const remaining = removeSelected();
      updateParent(remaining);
    }
  }, [selected, getSelectedText, removeSelected, updateParent]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text/plain');
    if (!pasted) return;

    // If there's selected text in the input, let native paste handle it
    if (inputRef.current && inputRef.current.selectionStart !== inputRef.current.selectionEnd) return;

    const tokens = tokenise(pasted);
    if (tokens.length === 0) return;

    // Check if all tokens would be complete pills
    const allComplete = tokens.every(t => isComplete(t));
    if (!allComplete) return; // Let native input handle partial text

    e.preventDefault();

    // Check disallow list for each token
    for (const token of tokens) {
      if (checkDisallowed) {
        const reason = checkDisallowed(token);
        if (reason) {
          setRejection(`"${token}" is not allowed: ${reason}`);
          return;
        }
      }
    }

    setRejection(null);
    const newPills = tokens.map(makePill);
    const result = [...pills, ...newPills];
    updateParent(result);
    clearSelection();
  }, [pills, checkDisallowed, updateParent, clearSelection]);

  // ── Drag and drop ──

  const handleDragStart = useCallback((e: React.DragEvent, index: number, pillId: string) => {
    dragSourceRef.current = index;

    // If dragging an unselected pill, select only it
    if (!selected.has(pillId)) {
      setSelected(new Set([pillId]));
      lastClickedRef.current = pillId;
    }

    // Serialize selected pills as text for external drag
    const text = pills.filter(p => selected.has(p.id) || p.id === pillId).map(p => p.text).join(' ');
    e.dataTransfer.setData('text/plain', text);
    e.dataTransfer.effectAllowed = 'copyMove';
  }, [pills, selected]);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    const sourceIndex = dragSourceRef.current;
    dragSourceRef.current = null;

    if (sourceIndex === null) {
      // External drop — parse text
      const text = e.dataTransfer.getData('text/plain');
      if (!text) return;
      const tokens = tokenise(text);
      const newPills = tokens.filter(t => isComplete(t)).map(makePill);
      if (newPills.length === 0) return;
      const result = [...pills];
      result.splice(dropIndex, 0, ...newPills);
      updateParent(result);
      clearSelection();
      return;
    }

    const isCopy = e.altKey;

    if (selected.size > 1) {
      // Multi-pill drag
      const selectedPills = pills.filter(p => selected.has(p.id));
      const remaining = pills.filter(p => !selected.has(p.id));

      if (isCopy) {
        // Copy: insert duplicates at drop position
        const copies = selectedPills.map(p => makePill(p.text));
        const result = [...pills];
        result.splice(dropIndex, 0, ...copies);
        updateParent(result);
      } else {
        // Move: figure out the adjusted insertion index
        const countBefore = pills.slice(0, dropIndex).filter(p => selected.has(p.id)).length;
        const insertAt = dropIndex - countBefore;
        remaining.splice(insertAt, 0, ...selectedPills);
        updateParent(remaining);
      }
    } else {
      // Single pill drag
      if (isCopy) {
        const result = [...pills];
        result.splice(dropIndex, 0, makePill(pills[sourceIndex].text));
        updateParent(result);
      } else {
        if (sourceIndex === dropIndex) return;
        updateParent(reorder(pills, sourceIndex, dropIndex));
      }
    }

    clearSelection();
  }, [pills, selected, updateParent, clearSelection]);

  const handleDragEnd = useCallback(() => {
    dragSourceRef.current = null;
    setDragOverIndex(null);
  }, []);

  // ── Pill add / remove ──

  const tryAdd = useCallback((text: string, currentPills: ArgPill[]): ArgPill[] | null => {
    if (checkDisallowed) {
      const reason = checkDisallowed(text);
      if (reason) {
        setRejection(`"${text}" is not allowed: ${reason}`);
        return null;
      }
    }
    setRejection(null);
    return [...currentPills, makePill(text)];
  }, [checkDisallowed]);

  const pillify = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || !isComplete(trimmed)) return false;
    const result = tryAdd(trimmed, pills);
    if (!result) return false;
    updateParent(result);
    setInputValue('');
    clearSelection();
    return true;
  }, [inputValue, pills, tryAdd, updateParent, clearSelection]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    if (val.endsWith(' ') && isBalanced(val)) {
      const trimmed = val.trim();
      if (isComplete(trimmed)) {
        const result = tryAdd(trimmed, pills);
        if (result) {
          updateParent(result);
          setInputValue('');
          clearSelection();
          return;
        }
        setInputValue(val);
        return;
      }
    }

    setInputValue(val);
  }, [pills, tryAdd, updateParent, clearSelection]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Delete selected pills
    if ((e.key === 'Backspace' || e.key === 'Delete') && selected.size > 0 && !inputValue) {
      e.preventDefault();
      const remaining = pills.filter(p => !selected.has(p.id));
      updateParent(remaining);
      clearSelection();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      pillify();
    } else if (e.key === 'Backspace' && !inputValue) {
      if (pills.length > 0) {
        const lastPill = pills[pills.length - 1];
        const newPills = pills.slice(0, -1);
        updateParent(newPills);
        setInputValue(lastPill.text);
        clearSelection();
      }
    } else if (e.key === 'a' && (e.metaKey || e.ctrlKey) && !inputValue) {
      // Ctrl/Cmd+A with empty input → select all pills
      e.preventDefault();
      setSelected(new Set(pills.map(p => p.id)));
    }
  }, [inputValue, pills, selected, pillify, updateParent, clearSelection]);

  const removePill = useCallback((removeId: string) => {
    const newPills = pills.filter(p => p.id !== removeId);
    updateParent(newPills);
    clearSelection();
    inputRef.current?.focus();
  }, [pills, updateParent, clearSelection]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    // Only clear selection and focus if clicking the container background
    if (e.target === containerRef.current || e.target === inputRef.current) {
      clearSelection();
      inputRef.current?.focus();
    }
  }, [clearSelection]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Don't pillify if focus is moving to another element within the container
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    pillify();
    clearSelection();
  }, [pillify, clearSelection]);

  // ── Render ──

  const pillClass = (pill: ArgPill) => {
    const classes = [styles.pill];
    if (pill.type === 'keyvalue') classes.push(styles.pillKeyValue);
    else if (pill.type === 'positional') classes.push(styles.pillPositional);
    else classes.push(styles.pillFlag);
    if (selected.has(pill.id)) classes.push(styles.pillSelected);
    return classes.join(' ');
  };

  return (
    <div>
      <div
        ref={containerRef}
        className={`${styles.container} ${rejection ? styles.containerError : ''}`}
        onClick={handleContainerClick}
        onKeyDown={handleContainerKeyDown}
        onPaste={handlePaste}
      >
        {pills.map((pill, index) => (
          editingId === pill.id ? (
            <span key={pill.id} className={`${styles.pill} ${styles.pillEditing}`}>
              <input
                ref={editInputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleEditKeyDown}
                onBlur={handleEditBlur}
                className={styles.pillEditInput}
                style={{ width: `${Math.max(editValue.length, 3)}ch` }}
                autoComplete="off"
                spellCheck={false}
              />
            </span>
          ) : (
            <span
              key={pill.id}
              className={`${pillClass(pill)} ${dragOverIndex === index ? styles.pillDropTarget : ''}`}
              draggable
              onClick={(e) => handlePillClick(e, pill.id)}
              onDoubleClick={(e) => { e.stopPropagation(); startEditing(pill.id); }}
              onDragStart={(e) => handleDragStart(e, index, pill.id)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <span className={styles.pillText}>{pill.text}</span>
              <button
                type="button"
                className={styles.pillRemove}
                onClick={(e) => { e.stopPropagation(); removePill(pill.id); }}
                tabIndex={-1}
              >
                ×
              </button>
            </span>
          )
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={pills.length === 0 ? '-flag -key=value positional' : ''}
          className={styles.input}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {rejection && (
        <span className={styles.rejection}>{rejection}</span>
      )}
    </div>
  );
}
